import { DASH } from '../utils/displayValues';
import type { TemplatePractitioner } from './templateUtils';

/** Mirrors document-renderer-service `labs-heuristics.rules.ts`. */
export const imagingCategoryPatterns: readonly RegExp[] = [
  /imaging/i,
  /radiolog/i,
  /\bmri\b/i,
  /\bmr\b/i,
  /\bxr\b/i,
  /\bcta\b/i,
  /\bct\b/i,
  /x-?ray/i,
  /ultra\s*sound/i,
  /sonogra?m/i,
  /scan/i,
  /mammo/i,
  /\bmammogram\b/i,
  /\bpet\b/i,
  /nuclear/i,
  /echocardiogram/i,
  /\becho\b/i,
  /angiogram/i,
  /fluoro/i,
];

const labsMeaninglessDisplayTokens = new Set([
  '',
  '-',
  'na',
  'n/a',
  'nan',
  'none',
  'null',
  'unknown',
  'unspecified',
  'not reported',
  'not available',
  'pending',
]);

const IMAGING_STUB_URN_PATTERN = /^urn:oid:1\.2\.840\.114350\.1\.72\.1\.5007$/i;

export type LabPanelCategory = 'lab' | 'imaging';

export interface LabsTemplateRow {
  labTest?: string | null;
  value?: string | null;
  units?: string;
  dateOfStudy?: string | null;
  interpretation?: string;
}

export interface LabsTemplatePanel {
  id: string;
  labPanel?: string | null;
  dateOfStudy?: string | null;
  rows: LabsTemplateRow[];
  interpreters: TemplatePractitioner[];
  performers: TemplatePractitioner[];
}

export interface ClassifiedLabsTemplatePanel extends LabsTemplatePanel {
  category: LabPanelCategory;
}

const normaliseString = (value?: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  return '';
};

/** Mirrors document-renderer-service `looksLikeImagingText`. */
export const looksLikeImagingText = (value?: string | null): boolean => {
  if (!value) return false;
  return imagingCategoryPatterns.some((pattern) => pattern.test(value));
};

/** Mirrors document-renderer-service `isMeaningfulValue`. */
export const isMeaningfulLabValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  const trimmed = normaliseString(value);
  if (!trimmed) return false;
  const canonical = trimmed.replace(/\s+/g, ' ').toLowerCase();
  return !labsMeaninglessDisplayTokens.has(canonical);
};

/** Mirrors document-renderer-service `displayOrDash` in `patient-labs-report.tsx`. */
export const LAB_TABLE_NOT_AVAILABLE = 'Not Available';

export const displayLabTableCell = (value?: unknown): string => {
  const text = normaliseString(value);
  if (!text || ['nan', 'unknown'].includes(text.toLowerCase())) {
    return LAB_TABLE_NOT_AVAILABLE;
  }
  return text;
};

const detectInferredCategory = (panel: LabsTemplatePanel): LabPanelCategory => {
  if (looksLikeImagingText(panel.labPanel)) {
    return 'imaging';
  }

  for (const row of panel.rows ?? []) {
    if (looksLikeImagingText(row.labTest) || looksLikeImagingText(row.value)) {
      return 'imaging';
    }
  }

  return 'lab';
};

const rowHasQuantitativeData = (row: LabsTemplateRow): boolean => {
  const value = normaliseString(row.value);
  const unit = normaliseString(row.units);
  const interpretation = normaliseString(row.interpretation);

  const meaningfulValue = isMeaningfulLabValue(value);
  const valueHasNumeric = /[-+]?\d/.test(value);
  const digitsOnlyValue = /^[-+]?\d+(?:\.\d+)?$/.test(value.trim());
  const unitMeaningful = isMeaningfulLabValue(unit) && !/^urn:/i.test(unit);
  const interpretationMeaningful = isMeaningfulLabValue(interpretation);

  if (meaningfulValue && (unitMeaningful || interpretationMeaningful)) {
    return true;
  }

  if (interpretationMeaningful) {
    return true;
  }

  if (valueHasNumeric) {
    if (unitMeaningful || interpretationMeaningful) {
      return true;
    }
    if (!digitsOnlyValue) {
      return true;
    }
    if (meaningfulValue && value.trim().length >= 4) {
      return true;
    }
  }

  return false;
};

const rowLooksLikeImagingStub = (row: LabsTemplateRow): boolean => {
  const labTest = row.labTest;
  const value = row.value;
  const unit = normaliseString(row.units);

  const nameMissing = labTest == null || !labTest.trim();
  const textMissing = value == null || !value.trim();
  if (!nameMissing || !textMissing) {
    return false;
  }

  return IMAGING_STUB_URN_PATTERN.test(unit);
};

/**
 * Mirrors document-renderer-service panel category assignment in
 * `labs-normalizer.service.ts`.
 */
export const classifyLabPanel = (
  panel: LabsTemplatePanel,
): LabPanelCategory => {
  const rows = panel.rows ?? [];
  const inferredCategory = detectInferredCategory(panel);
  const hasQuantitativeData = rows.some(rowHasQuantitativeData);
  const stubDetected = rows.some(rowLooksLikeImagingStub);

  return inferredCategory === 'imaging' &&
    (stubDetected || !hasQuantitativeData)
    ? 'imaging'
    : 'lab';
};

export const classifyLabPanels = (
  panels: LabsTemplatePanel[],
): ClassifiedLabsTemplatePanel[] =>
  panels.map((panel) => ({
    ...panel,
    category: classifyLabPanel(panel),
  }));

const normaliseRowName = (row: LabsTemplateRow): string => {
  const trimmed = normaliseString(row.labTest);
  return trimmed.length > 0 ? trimmed : DASH;
};

const dedupeNames = (names: ReadonlyArray<string>): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const name of names) {
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
};

export const isOutsideReferenceRange = (row: LabsTemplateRow): boolean => {
  const interpretation = normaliseString(row.interpretation).toLowerCase();
  return (
    interpretation.includes('out') ||
    interpretation === 'high' ||
    interpretation === 'low' ||
    interpretation === 'abnormal' ||
    interpretation.includes('critical')
  );
};

/**
 * Mirrors document-renderer-service `meetsCoreFieldRule` in
 * `prepareGroupsForDisplay` (omits placeholder rows with invalid name/unit
 * and value `16`, plus `comment` values).
 */
export const rowMeetsCoreFieldRule = (row: LabsTemplateRow): boolean => {
  const unitDisplay = normaliseString(row.units);
  const valueRaw = normaliseString(row.value);
  if (valueRaw.toLowerCase() === 'comment') return false;

  const nameMeaningful = isMeaningfulLabValue(row.labTest);
  const unitMeaningful = isMeaningfulLabValue(unitDisplay);
  const numericValue = Number(valueRaw);
  const valueIs16 =
    valueRaw.toLowerCase() === '16' ||
    (Number.isFinite(numericValue) && numericValue === 16);

  return !(!nameMeaningful && !unitMeaningful && valueIs16);
};

export const partitionLabPanelRows = (
  rows: ReadonlyArray<LabsTemplateRow>,
): {
  rowsWithData: LabsTemplateRow[];
} => ({
  rowsWithData: rows.filter(rowMeetsCoreFieldRule),
});

export interface LabPanelDisplaySummary {
  totalLabs: number;
  outsideCount: number;
  outsideNames: ReadonlyArray<string>;
}

/** Mirrors document-renderer-service `buildPanelSummary`. */
export const buildLabPanelSummary = (
  rowsWithData: ReadonlyArray<LabsTemplateRow>,
): LabPanelDisplaySummary => {
  const outsideNames = dedupeNames(
    rowsWithData.filter(isOutsideReferenceRange).map(normaliseRowName),
  );

  return {
    totalLabs: rowsWithData.length,
    // Deduplicated count – mirrors document-renderer-service `buildPanelSummary`.
    outsideCount: outsideNames.length,
    outsideNames,
  };
};

export const panelHasDisplayableRows = (
  panel: Pick<LabsTemplatePanel, 'rows'>,
): boolean => (panel.rows ?? []).some(rowMeetsCoreFieldRule);

export const panelShouldDisplay = (
  panel: ClassifiedLabsTemplatePanel,
): boolean => {
  if ((panel.rows?.length ?? 0) === 0) return false;
  if (panel.category === 'imaging') return true;
  return panelHasDisplayableRows(panel);
};

export interface LabTocSummaryItem {
  id: string;
  label: string;
  anchorId: string;
  count: number;
}

/** Mirrors document-renderer-service `aggregateToc`. */
export const aggregateLabToc = (
  panels: ClassifiedLabsTemplatePanel[],
  category: LabPanelCategory,
): LabTocSummaryItem[] => {
  const normalize = (value: string) => value.trim().toLowerCase();
  const grouped = new Map<string, LabTocSummaryItem>();

  panels.forEach((panel, index) => {
    const isImaging = panel.category === 'imaging';
    if ((category === 'imaging') !== isImaging) return;

    const raw = panel.labPanel?.trim();
    const key = raw ? `name-${normalize(raw)}` : `panel-${panel.id}`;
    const anchorId = `lab-panel-${panel.id || index}`;
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, {
        id: `${category}-${key}`,
        label: raw || DASH,
        anchorId,
        count: 1,
      });
      return;
    }

    existing.count += 1;
  });

  return Array.from(grouped.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
};

export const countPanelsByCategory = (
  panels: ClassifiedLabsTemplatePanel[],
  category: LabPanelCategory,
): number =>
  panels.filter((panel) =>
    category === 'imaging'
      ? panel.category === 'imaging'
      : panel.category !== 'imaging',
  ).length;
